import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Globe, MapPin, Users, MailPlus, Download } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useSEO } from "@/lib/seo";
import AdminTabs from "@/components/AdminTabs";

const COUNTRY_LABEL = {
  US: "United States",
  CA: "Canada",
  GB: "United Kingdom",
  AU: "Australia",
  DE: "Germany",
  FR: "France",
  ES: "Spain",
  IT: "Italy",
  IN: "India",
  MX: "Mexico",
  OTHER: "Other / not listed",
};

function fmt(n) {
  return new Intl.NumberFormat().format(n || 0);
}

function copyToClipboard(text) {
  if (!navigator?.clipboard) return;
  navigator.clipboard.writeText(text).catch(() => {});
}

export default function AdminRegion() {
  useSEO({ title: "Admin · Region & waitlist", noindex: true });

  const [stats, setStats] = useState(null);
  const [waitlist, setWaitlist] = useState([]);
  const [filterCountry, setFilterCountry] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  const reload = async () => {
    setError(""); setLoading(true);
    try {
      const [s, w] = await Promise.all([
        api.get("/admin/region/stats?days=90"),
        api.get(`/admin/region/waitlist?limit=200${filterCountry ? `&country=${filterCountry}` : ""}`),
      ]);
      setStats(s.data || null);
      setWaitlist(w.data?.items || []);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [filterCountry]);

  const exportCsv = () => {
    if (!waitlist.length) return;
    const header = ["email", "name", "country", "state_province", "ts", "notified"];
    const rows = waitlist.map((r) => header.map((k) => `"${(r[k] ?? "").toString().replaceAll('"', '""')}"`).join(","));
    const csv = [header.join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `sonspeak-waitlist-${filterCountry || "all"}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  return (
    <div className="min-h-screen bg-[color:var(--cream)] p-4 md:p-8">
      <div className="max-w-6xl mx-auto">
        <Link to="/profiles" className="pill-btn mb-4 inline-flex" data-testid="region-back">
          <ArrowLeft size={14} /> Back
        </Link>
        <h1 className="display text-3xl md:text-4xl font-extrabold mb-1 flex items-center gap-2">
          <Globe size={28} /> Region &amp; waitlist
        </h1>
        <p className="text-[color:var(--ink-soft)] mb-6 max-w-2xl">
          Live geographic demand. Every blocked non-US signup is captured here
          so when the lawyer clears a country, we already have the email list.
          Zero-spend marketing.
        </p>

        <AdminTabs current="region" />

        {error && (
          <div className="brutal-card bg-rose-50 text-[color:var(--coral-deep)] p-3 mb-4" data-testid="region-error">{error}</div>
        )}
        {loading && !stats && (
          <div className="text-[color:var(--ink-soft)]">Loading…</div>
        )}

        {stats && (
          <>
            {/* Top KPIs */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
              <div className="brutal-card bg-white p-4" data-testid="region-kpi-success">
                <div className="text-xs uppercase text-[color:var(--ink-soft)] tracking-wider font-bold flex items-center gap-1.5">
                  <Users size={12} /> Signups (US)
                </div>
                <div className="display text-3xl font-extrabold mt-1">
                  {fmt((stats.success_by_country.find((c) => c.country === "US") || {}).count)}
                </div>
                <div className="text-[11px] text-[color:var(--ink-soft)] mt-0.5">last {stats.window_days}d</div>
              </div>
              <div className="brutal-card bg-[color:var(--gold)] p-4" data-testid="region-kpi-waitlist">
                <div className="text-xs uppercase tracking-wider font-bold flex items-center gap-1.5">
                  <MailPlus size={12} /> Waitlist (all)
                </div>
                <div className="display text-3xl font-extrabold mt-1">{fmt(stats.waitlist_total)}</div>
                <div className="text-[11px] mt-0.5">{fmt(stats.waitlist_recent)} in last {stats.window_days}d</div>
              </div>
              <div className="brutal-card bg-white p-4" data-testid="region-kpi-countries">
                <div className="text-xs uppercase text-[color:var(--ink-soft)] tracking-wider font-bold flex items-center gap-1.5">
                  <Globe size={12} /> Countries asking
                </div>
                <div className="display text-3xl font-extrabold mt-1">{fmt(stats.waitlist_by_country.length)}</div>
                <div className="text-[11px] text-[color:var(--ink-soft)] mt-0.5">unique non-US countries</div>
              </div>
              <div className="brutal-card bg-white p-4" data-testid="region-kpi-top-country">
                <div className="text-xs uppercase text-[color:var(--ink-soft)] tracking-wider font-bold flex items-center gap-1.5">
                  <MapPin size={12} /> Top non-US demand
                </div>
                <div className="display text-2xl font-extrabold mt-1">
                  {stats.waitlist_by_country[0]?.country
                    ? `${COUNTRY_LABEL[stats.waitlist_by_country[0].country] || stats.waitlist_by_country[0].country}`
                    : "—"}
                </div>
                <div className="text-[11px] text-[color:var(--ink-soft)] mt-0.5">
                  {fmt(stats.waitlist_by_country[0]?.count)} on waitlist
                </div>
              </div>
            </div>

            {/* Waitlist by country */}
            <section className="brutal-card bg-white p-5 mb-6" data-testid="region-by-country">
              <h2 className="display text-xl font-extrabold mb-3 flex items-center gap-2">
                <MailPlus size={18} /> Waitlist by country
              </h2>
              <p className="text-sm text-[color:var(--ink-soft)] mb-3">
                Click a country to filter the waitlist below, then export the
                CSV for an Email-everyone-on-the-list campaign the moment the
                lawyer clears that region.
              </p>
              {stats.waitlist_by_country.length === 0 ? (
                <div className="text-sm text-[color:var(--ink-soft)] italic">No blocked signups yet.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b-2 border-[color:var(--ink)]">
                        <th className="text-left py-2 pr-3">Country</th>
                        <th className="text-left py-2 pr-3">On waitlist</th>
                        <th className="text-left py-2 pr-3">Last seen</th>
                        <th className="text-right py-2"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {stats.waitlist_by_country.map((r) => (
                        <tr key={r.country} className="border-b border-[color:var(--paper-muted)]">
                          <td className="py-2 pr-3 font-semibold">
                            {COUNTRY_LABEL[r.country] || r.country}{" "}
                            <span className="text-[11px] text-[color:var(--ink-soft)]">({r.country})</span>
                          </td>
                          <td className="py-2 pr-3">{fmt(r.count)}</td>
                          <td className="py-2 pr-3 text-[color:var(--ink-soft)]">
                            {r.last_seen ? new Date(r.last_seen).toLocaleString() : "—"}
                          </td>
                          <td className="py-2 text-right">
                            <button
                              type="button"
                              onClick={() => setFilterCountry(r.country)}
                              className="pill-btn text-xs"
                              data-testid={`region-filter-${r.country}`}
                            >
                              Filter
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>

            {/* US states leaderboard — heatmap-without-a-map */}
            {stats.success_by_state.length > 0 && (
              <section className="brutal-card bg-white p-5 mb-6" data-testid="region-us-states">
                <h2 className="display text-xl font-extrabold mb-3 flex items-center gap-2">
                  <MapPin size={18} /> Top US states (signups, last {stats.window_days}d)
                </h2>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                  {stats.success_by_state.map((s) => (
                    <div key={s.state} className="brutal-card p-2 bg-[color:var(--paper-muted)] text-center">
                      <div className="display text-2xl font-extrabold">{fmt(s.count)}</div>
                      <div className="text-xs uppercase tracking-wider text-[color:var(--ink-soft)] font-bold">{s.state}</div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Waitlist drilldown */}
            <section className="brutal-card bg-white p-5" data-testid="region-waitlist-list">
              <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
                <h2 className="display text-xl font-extrabold flex items-center gap-2">
                  <Users size={18} /> Waitlist
                  {filterCountry && (
                    <span className="text-sm font-normal text-[color:var(--ink-soft)]">
                      · filtered to {COUNTRY_LABEL[filterCountry] || filterCountry}{" "}
                      <button
                        type="button"
                        onClick={() => setFilterCountry("")}
                        className="underline ml-1"
                        data-testid="region-filter-clear"
                      >
                        clear
                      </button>
                    </span>
                  )}
                </h2>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={exportCsv}
                    className="pill-btn"
                    disabled={waitlist.length === 0}
                    data-testid="region-export-csv"
                  >
                    <Download size={14} /> Export CSV
                  </button>
                  <button
                    type="button"
                    onClick={() => copyToClipboard(waitlist.map((r) => r.email).join(","))}
                    className="pill-btn"
                    disabled={waitlist.length === 0}
                    data-testid="region-copy-emails"
                  >
                    Copy emails
                  </button>
                </div>
              </div>
              {waitlist.length === 0 ? (
                <div className="text-sm text-[color:var(--ink-soft)] italic">No waitlist entries{filterCountry ? ` for ${filterCountry}` : ""} yet.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b-2 border-[color:var(--ink)]">
                        <th className="text-left py-2 pr-3">Email</th>
                        <th className="text-left py-2 pr-3">Name</th>
                        <th className="text-left py-2 pr-3">Country</th>
                        <th className="text-left py-2 pr-3">When</th>
                        <th className="text-left py-2 pr-3">Notified?</th>
                      </tr>
                    </thead>
                    <tbody>
                      {waitlist.map((r) => (
                        <tr key={r.email} className="border-b border-[color:var(--paper-muted)]" data-testid={`region-row-${r.email}`}>
                          <td className="py-2 pr-3 font-mono text-xs">{r.email}</td>
                          <td className="py-2 pr-3">{r.name || "—"}</td>
                          <td className="py-2 pr-3">{r.country}{r.state_province ? `/${r.state_province}` : ""}</td>
                          <td className="py-2 pr-3 text-[color:var(--ink-soft)]">{new Date(r.ts).toLocaleString()}</td>
                          <td className="py-2 pr-3">
                            {r.notified ? (
                              <span className="text-emerald-700 text-xs font-bold">✓ emailed</span>
                            ) : (
                              <span className="text-[color:var(--ink-soft)] text-xs">pending</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          </>
        )}
      </div>
    </div>
  );
}
